// Styles for CardOverviewVertical

import styled from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';

const styleConstants = {
  // Image dimensions for vertical card layout (featured cards)
  imageDimensionVertical: {
    desktopWidth: 190,
    mobileWidth: 190,
    tabletWidth: 202,
  },
  // Image dimensions for default/BLP layout
  imageDimensionDefault: {
    desktopWidth: 221,
    tabletWidth: 171,
    mobileWidth: 110,
  },
  // Image dimensions for 5-card grid layout
  imageDimensionFiveCard: {
    desktopWidth: 168,
  },
  // Card container dimensions for default layout
  cardDimensionDefault: {
    desktopWidth: 370,
    tabletWidth: 343,
    mobileWidth: 320,
  },
  // Card container dimensions for 5-card grid
  fiveCardDimension: {
    desktopWidth: 188,
    tabletWidth: 324,
    mobileWidth: 295,
  },
  // Card container dimensions for 3-card layout
  cardDimensionThree: {
    desktopWidth: 352,
  },
  // Negative margin for one-column layout (card overlapping)
  oneColumn: {
    desktopTop: -132,
    tabletTop: -108,
    mobileTop: -80,
  },
  // Negative margin for three-column layout
  threeColumn: {
    tabletTop: -45,
  },
} as const;

/**
 * Main styled component for CardOverviewVertical
 * Extends base card overview styles with vertical-specific variations
 */
export const StyledCardOverviewVertical = styled.div`
  /* ===== Card Container Base Styles ===== */
  .card-overview {
    &-bordered {
      border: ${baseVariables.border.borderWidth02} ${baseVariables.border.borderStyleDefault}
        ${baseVariables.color.neutral20};
      border-radius: ${toRem(16)};
      box-shadow: 0 ${toRem(4)} ${toRem(20)} ${baseVariables.shadows.boxShadowTile};
      position: relative;
      width: 100%;
      white-space: normal;
      max-width: ${toRem(styleConstants.cardDimensionDefault.mobileWidth)};

      @media ${baseVariables.mediaQuery.md} {
        max-width: ${toRem(styleConstants.cardDimensionDefault.tabletWidth)};
      }

      @media ${baseVariables.mediaQuery.lg} {
        max-width: ${toRem(styleConstants.cardDimensionDefault.desktopWidth)};
        border-radius: ${baseVariables.border.borderInset02};
      }

      /* Image container for vertical layout */
      &__image {
        max-width: ${toRem(202)};
      }

      /* Default card container sizing */
      &__default {
        max-width: ${toRem(styleConstants.cardDimensionDefault.mobileWidth)};

        @media ${baseVariables.mediaQuery.md} {
          max-width: ${toRem(styleConstants.cardDimensionDefault.tabletWidth)};
        }

        @media ${baseVariables.mediaQuery.lg} {
          max-width: ${toRem(styleConstants.cardDimensionDefault.desktopWidth)};
        }
      }

      /* Desktop-only content visibility */
      .displayOnDesktop {
        display: none;

        @media ${baseVariables.mediaQuery.lg} {
          display: inline-block;
        }
      }

      /* Horizontal rule separators */
      hr {
        border: none;
        border-bottom: ${baseVariables.border.borderWidth02} ${baseVariables.border.borderStyleDefault}
          ${baseVariables.color.neutral30};
        width: 100%;
      }
      .points-underlined {
        font-family: ${baseVariables.font.fontSwissCondensed};
        border-bottom: ${toRem(5)} ${baseVariables.border.borderStyleDefault} ${baseVariables.color.accent10};
        display: block;
        width: fit-content;
        margin: ${toRem(4)} auto ${toRem(8)} auto;
        @media ${baseVariables.mediaQuery.md} {
          margin: ${toRem(8)} auto ${toRem(12)} auto;
        }
        @media ${baseVariables.mediaQuery.lg} {
          margin: 0;
          display: inline;
        }
      }
    }

    /* ===== Layout Variations ===== */
    /* Two-card layout */
    &-two-cards {
      @media ${baseVariables.mediaQuery.lg} {
        width: ${toRem(styleConstants.cardDimensionDefault.desktopWidth)};
      }
    }

    /* Featured cards layout */
    &-featured-cards {
      @media ${baseVariables.mediaQuery.lg} {
        width: 100%;
        max-width: ${toRem(461)};
      }
    }

    /* Three-card layout */
    &-three-cards {
      @media ${baseVariables.mediaQuery.lg} {
        width: ${toRem(styleConstants.cardDimensionThree.desktopWidth)};
      }
    }

    /* Five-card grid layout (no borders) */
    &-no-border {
      border: none;
      border-radius: 0;
      box-shadow: none;
      max-width: ${toRem(styleConstants.fiveCardDimension.mobileWidth)};
      @media ${baseVariables.mediaQuery.md} {
        max-width: ${toRem(styleConstants.fiveCardDimension.tabletWidth)};
      }
      @media ${baseVariables.mediaQuery.xl} {
        max-width: ${toRem(styleConstants.fiveCardDimension.desktopWidth)};
      }

      &-image {
        width: ${toRem(styleConstants.imageDimensionFiveCard.desktopWidth)};
      }
    }

    /* Business card specific styling for 5-card layout */
    &-business-card {
      /* Additional styles can be added here if needed */
    }

    /* ===== Content Section Styles ===== */

    /* Card header styling */
    &-header {
      p {
        margin-bottom: ${toRem(8)};
        @media ${baseVariables.mediaQuery.lg} {
          margin-bottom: ${toRem(16)};
        }
      }
    }

    /* Card subheader and points section styling */
    &-subheader {
      p {
        &:first-child {
          margin-bottom: ${toRem(8)};
          @media ${baseVariables.mediaQuery.md} {
            margin-bottom: ${toRem(16)};
          }
          @media ${baseVariables.mediaQuery.lg} {
            margin-bottom: ${toRem(32)};
          }
        }
        &:last-child {
          margin-top: auto;
        }
      }
      & > div {
        display: flex;
        flex-direction: column;
      }
      .line-separator {
        display: none;
        @media ${baseVariables.mediaQuery.lg} {
          display: inline;
        }
      }
      .link-wrapper {
        @media ${baseVariables.mediaQuery.lg} {
          margin-top: ${toRem(8)};
          display: inline-block;
        }
      }
      .custom-link {
        text-decoration: underline;
        display: block;
        @media ${baseVariables.mediaQuery.lg} {
          display: inline;
        }
      }
      .points-red {
        color: ${baseVariables.color.alert40};
      }
      .middle-align {
        vertical-align: unset;
        @media ${baseVariables.mediaQuery.lg} {
          vertical-align: super;
        }
      }
    }

    /* ===== Vertical Card Layout (Featured on Card) ===== */
    &-foc {
      height: 100%;
      &-wrapper,
      &-wrapper .card-overview-wrapper {
        width: 100%;
        .card-overview-subheader {
          & > div {
            justify-content: center;
            flex-grow: 1;
          }
        }
      }
      p {
        &:last-child {
          margin: 0;
        }
      }

      &-image {
        margin-top: ${styleConstants.threeColumn.tabletTop}%;
        width: ${toRem(styleConstants.imageDimensionVertical.mobileWidth)};
        @media ${baseVariables.mediaQuery.md} {
          width: ${toRem(styleConstants.imageDimensionVertical.tabletWidth)};
        }
        @media ${baseVariables.mediaQuery.lg} {
          width: ${toRem(styleConstants.imageDimensionVertical.desktopWidth)};
        }
      }
      p {
        &:first-child {
          margin-bottom: ${toRem(8)};
          margin-top: ${toRem(0)};
        }
      }
      .custom-link {
        @media ${baseVariables.mediaQuery.md} {
          display: inline;
        }
        @media ${baseVariables.mediaQuery.lg} {
          display: block;
        }
      }
      .line-separator {
        display: none;
        @media ${baseVariables.mediaQuery.md} {
          display: inline;
        }
        @media ${baseVariables.mediaQuery.lg} {
          display: none;
        }
      }
      .points-underlined {
        display: inline;
      }
    }

    /* ===== BLP (Brand Landing Page) Layout ===== */
    &-blp {
      &-image {
        width: ${toRem(styleConstants.imageDimensionDefault.mobileWidth)};
        margin-top: ${toRem(styleConstants.oneColumn.mobileTop)};
        @media ${baseVariables.mediaQuery.md} {
          width: ${toRem(styleConstants.imageDimensionDefault.tabletWidth)};
          margin-top: ${toRem(styleConstants.oneColumn.tabletTop)};
        }
        @media ${baseVariables.mediaQuery.lg} {
          width: ${toRem(styleConstants.imageDimensionDefault.desktopWidth)};
          margin-top: ${toRem(styleConstants.oneColumn.desktopTop)};
        }
      }
    }
  }

  /* ===== Utility Classes ===== */

  /* Flex centering utility */
  .flex-align-center {
    display: flex;
    align-items: center;
    justify-content: center;
    @media ${baseVariables.mediaQuery.md} {
      br {
        display: none;
      }
    }
  }

  /* Checkbox wrapper container */
  .checkbox-wrapper {
    height: ${toRem(24)};
  }

  /* Remove margins from paragraphs */
  .margin-zero {
    p {
      margin: 0;
    }
  }

  /* Tertiary links (terms, rates, fees) styling */
  .tertiaryLinks_styles {
    a {
      text-decoration: underline;
      font-size: ${toRem(16)};
      font-weight: 500;
      text-underline-offset: ${toRem(2)};
    }
  }
`;
